import React, { useState, useEffect } from 'react';
import { array, arrayOf, bool, func, number, oneOf, shape, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import { getUrls } from './TransactionPage.duck';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { createResourceLocatorString, findRouteByRouteName } from '../../util/routes';
import { propTypes } from '../../util/types';
import { ensureListing, ensureTransaction } from '../../util/data';
import { timeOfDayFromTimeZoneToLocal } from '../../util/dates';
import { createSlug } from '../../util/urlHelpers';
import { txIsPaymentPending } from '../../util/transaction';
import routeConfiguration from '../../routing/routeConfiguration';

import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/UI.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';
import { ShopNav } from '../../components';
import {
  NamedRedirect,
  Page,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  UserDisplayName,
  SecondaryButton,
  LayoutSideNavigation,
  LayoutWrapperSideNav
} from '../../components';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';

import DisputeModal from './DisputeModal/DisputeModal';
import ReviewModal from './ReviewModal/ReviewModal';
import TransactionPanel from './TransactionPanel/TransactionPanel';

import {
  dispute,
  markReceived,
  markReceivedFromPurchased,
  markDelivered,
  sendMessage,
  sendReview,
  fetchMoreMessages,
  fetchTransactionLineItems,
} from './TransactionPage.duck';
import css from './TransactionPage.module.css';

const PROVIDER = 'provider';
const CUSTOMER = 'customer';

// TransactionPage handles data loading for Sale and Order views to transaction pages in Inbox.
export const TransactionPageComponent = props => {
  const [state, setState] = useState({
    isDisputeModalOpen: false,
    disputeSubmitted: false,
    isReviewModalOpen: false,
    reviewSubmitted: false,
  });
  const {
    currentUser,
    initialMessageFailedToTransaction,
    savePaymentMethodFailed,
    fetchMessagesError,
    fetchMessagesInProgress,
    totalMessagePages,
    oldestMessagePageFetched,
    fetchTransactionError,
    history,
    intl,
    messages,
    onManageDisableScrolling,
    onSendMessage,
    onSendReview,
    onShowMoreMessages,
    params,
    scrollingDisabled,
    sendMessageError,
    sendMessageInProgress,
    sendReviewError,
    sendReviewInProgress,
    transaction,
    transactionRole,
    disputeInProgress,
    disputeError,
    onDispute,
    markReceivedInProgress,
    markReceivedError,
    onMarkReceived,
    markReceivedFromPurchasedInProgress,
    markReceivedFromPurchasedError,
    onMarkReceivedFromPurchased,
    markDeliveredInProgress,
    markDeliveredError,
    onMarkDelivered,
    timeSlots,
    fetchTimeSlotsError,
    processTransitions,
    callSetInitialValues,
    onInitializeCardPaymentData,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    urls,
    getUrls,
  } = props;
  const currentTransaction = ensureTransaction(transaction);
  const currentListing = ensureListing(currentTransaction.listing);
  const isProviderRole = transactionRole === PROVIDER;
  const isCustomerRole = transactionRole === CUSTOMER;
  const isPaymentRequired = currentTransaction?.attributes?.payinTotal?.amount > 0;
  const currentProducts = currentTransaction?.attributes?.protectedData?.products || [];
  const isShippingRequired = currentProducts.some(product => product.shippingRequired);
  const hasDigitalGoodUrls = currentProducts.some(
    product => product.productType === 'digital' && product.digitalGoodType === 'link'
  );

  useEffect(() => {
    if (!!transaction && hasDigitalGoodUrls &&  Object.keys(urls).length == 0 ) {
      const productsWithUrls = currentProducts.filter(
        item => item.productType === 'digital' && item.digitalGoodType === 'link'
      );
      const listingIds = productsWithUrls.map(item => item.id);
      getUrls(listingIds, transaction?.id?.uuid, currentUser.id.uuid);
    }
  }, [transaction]);

  const redirectToCheckoutPageWithInitialValues = (initialValues, listing) => {
    const routes = routeConfiguration();
    // Customize checkout page state with current listing and selected bookingDates
    const { setInitialValues } = findRouteByRouteName('CheckoutPage', routes);
    callSetInitialValues(setInitialValues, initialValues);

    // Clear previous Stripe errors from store if there is any
    onInitializeCardPaymentData();

    // Redirect to CheckoutPage
    history.push(
      createResourceLocatorString(
        'CheckoutPage',
        routes,
        {
          id: currentListing.id.uuid,
          slug: createSlug(currentListing.attributes.title),
        },
        {}
      )
    );
  };

  // Customer can create a booking, if the tx is in "enquiry" state.
  const handleSubmitOrderRequest = values => {
    const { bookingDates, quantity: quantityRaw, ...otherOrderData } = values;
    const bookingDatesMaybe = bookingDates
      ? {
          bookingDates: {
            bookingStart: bookingDates.startDate,
            bookingEnd: bookingDates.endDate,
          },
        }
      : {};

    const initialValues = {
      listing: currentListing,
      // enquired transaction should be passed to CheckoutPage
      transaction: currentTransaction,
      orderData: {
        ...bookingDatesMaybe,
        quantity: Number.parseInt(quantityRaw, 10),
        ...otherOrderData,
      },
      confirmPaymentError: null,
    };

    redirectToCheckoutPageWithInitialValues(initialValues, currentListing);
  };

  // Open review modal
  // This is called from ActivityFeed and from action buttons
  const onOpenReviewModal = () => {
    setState(prevState => ({
      ...prevState,
      isReviewModalOpen: true,
    }));
  };

  // Submit review and close the review modal
  const onSubmitReview = values => {
    const { reviewRating, reviewContent } = values;
    const rating = Number.parseInt(reviewRating, 10);
    onSendReview(transactionRole, currentTransaction, rating, reviewContent)
      .then(r =>
        setState(prevState => ({
          ...prevState,
          isReviewModalOpen: false,
          reviewSubmitted: true,
        }))
      )
      .catch(e => {
        // Do nothing.
      });
  };

  // Open dispute modal
  const onOpenDisputeModal = () => {
    setState(prevState => ({
      ...prevState,
      isDisputeModalOpen: true,
    }));
  };
  // Submit dispute and close the review modal
  const onDisputeOrder = values => {
    const { disputeReason } = values;
    onDispute(currentTransaction.id, disputeReason)
      .then(r => {
        return setState(prevState => ({
          ...prevState,
          disputeSubmitted: true,
        }));
      })
      .catch(e => {
        // Do nothing.
      });
  };

  const deletedListingTitle = intl.formatMessage({
    id: 'TransactionPage.deletedListing',
  });
  const listingTitle = currentListing.attributes.deleted
    ? deletedListingTitle
    : currentListing.attributes.title;

  // Redirect users with someone else's direct link to their own inbox/sales or inbox/orders page.
  const isDataAvailable =
    currentUser &&
    currentTransaction.id &&
    currentTransaction.id.uuid === params.id &&
    currentTransaction.attributes.lineItems &&
    currentTransaction.customer &&
    currentTransaction.provider &&
    !fetchTransactionError;

  const isShippable = true;

  const isOwnSale =
    isDataAvailable &&
    isProviderRole &&
    currentUser.id.uuid === currentTransaction.provider.id.uuid;
  const isOwnOrder =
    isDataAvailable &&
    isCustomerRole &&
    currentUser.id.uuid === currentTransaction.customer.id.uuid;

  if (isDataAvailable && isProviderRole && !isOwnSale) {
    // eslint-disable-next-line no-console
    console.error('Tried to access a sale that was not owned by the current user');
    return <NamedRedirect name="InboxPageSeller" params={{ tab: 'sales' }} />;
  } else if (isDataAvailable && isCustomerRole && !isOwnOrder) {
    // eslint-disable-next-line no-console
    console.error('Tried to access an order that was not owned by the current user');
    return <NamedRedirect name="InboxPageSeller" params={{ tab: 'orders' }} />;
  }

  const detailsClassName = classNames(css.tabContent, css.tabContentVisible);

  const fetchErrorMessage = isCustomerRole
    ? 'TransactionPage.fetchOrderFailed'
    : 'TransactionPage.fetchSaleFailed';
  const loadingMessage = isCustomerRole
    ? 'TransactionPage.loadingOrderData'
    : 'TransactionPage.loadingSaleData';

  const loadingOrFailedFetching = fetchTransactionError ? (
    <p className={css.error}>
      <FormattedMessage id={`${fetchErrorMessage}`} />
    </p>
  ) : (
    <p className={css.loading}>
      <FormattedMessage id={`${loadingMessage}`} />
    </p>
  );

  const initialMessageFailed = !!(
    initialMessageFailedToTransaction &&
    currentTransaction.id &&
    initialMessageFailedToTransaction.uuid === currentTransaction.id.uuid
  );

  const otherUserDisplayName = isOwnOrder ? (
    <UserDisplayName user={currentTransaction.provider} intl={intl} />
  ) : (
    <UserDisplayName user={currentTransaction.customer} intl={intl} />
  );

  const openFullfillmentPage = () => {
    const routes = routeConfiguration();
    // Customize checkout page state with current listing and selected bookingDates

    // Clear previous Stripe errors from store if there is any
    onInitializeCardPaymentData();

    // Redirect to CheckoutPage
    history.push(
      createResourceLocatorString('SaleFullfillPage', routes, { id: transaction.id.uuid }, {})
    );
  };
    const goBack = () => {
    const routes = routeConfiguration();
    // Customize checkout page state with current listing and selected bookingDates

    // Clear previous Stripe errors from store if there is any
    onInitializeCardPaymentData();

    // Redirect to SellerOrders
    if (isProviderRole) {
      history.push(createResourceLocatorString('InboxPageSeller', routes, {}, {}));
    } else {
      history.push(createResourceLocatorString('InboxPageBuyer', routes, {}, {}));
    }
  };

  const backtext = '< Back';
  // TransactionPanel is presentational component
  // that currently handles showing everything inside layout's main view area.
  const panel = isDataAvailable ? (
    <TransactionPanel
      onGoBack={goBack}
      transactionId={transaction?.id?.uuid}
      isFullfillment={false}
      className={detailsClassName}
      currentUser={currentUser}
      transaction={currentTransaction}
      fetchMessagesInProgress={fetchMessagesInProgress}
      totalMessagePages={totalMessagePages}
      oldestMessagePageFetched={oldestMessagePageFetched}
      messages={messages}
      initialMessageFailed={initialMessageFailed}
      savePaymentMethodFailed={savePaymentMethodFailed}
      fetchMessagesError={fetchMessagesError}
      sendMessageInProgress={sendMessageInProgress}
      sendMessageError={sendMessageError}
      onManageDisableScrolling={onManageDisableScrolling}
      onShowMoreMessages={onShowMoreMessages}
      onSendMessage={onSendMessage}
      onOpenReviewModal={onOpenReviewModal}
      onOpenDisputeModal={onOpenDisputeModal}
      transactionRole={transactionRole}
      markReceivedProps={{
        inProgress: markReceivedInProgress,
        error: markReceivedError,
        onTransition: () => onMarkReceived(currentTransaction.id),
        buttonText: intl.formatMessage({
          id: 'TransactionPage.markReceived.actionButton',
        }),
        errorText: intl.formatMessage({
          id: 'TransactionPage.markReceived.actionError',
        }),
      }}
      markReceivedFromPurchasedProps={{
        inProgress: markReceivedFromPurchasedInProgress,
        error: markReceivedFromPurchasedError,
        onTransition: () => onMarkReceivedFromPurchased(currentTransaction.id),
        buttonText: intl.formatMessage({
          id: 'TransactionPage.markReceivedFromPurchased.actionButton',
        }),
        errorText: intl.formatMessage({
          id: 'TransactionPage.markReceivedFromPurchased.actionError',
        }),
      }}
      markDeliveredProps={{
        inProgress: markDeliveredInProgress,
        error: markDeliveredError,
        onTransition: () => openFullfillmentPage(), //onMarkDelivered(currentTransaction.id, {trackingNumber : {trackingNumber}}),
        buttonText: intl.formatMessage({
          id: isShippable
            ? 'TransactionPage.markShipped.actionButton'
            : 'TransactionPage.markDelivered.actionButton',
        }),
        errorText: intl.formatMessage({
          id: 'TransactionPage.markDelivered.actionError',
        }),
      }}
      leaveReviewProps={{
        inProgress: sendReviewInProgress,
        error: sendReviewError,
        onTransition: onOpenReviewModal,
        buttonText: intl.formatMessage({
          id: 'TransactionPage.leaveReview.actionButton',
        }),
        errorText: intl.formatMessage({
          id: 'TransactionPage.leaveReview.actionError',
        }),
      }}
      nextTransitions={processTransitions}
      onSubmitOrderRequest={handleSubmitOrderRequest}
      timeSlots={timeSlots}
      fetchTimeSlotsError={fetchTimeSlotsError}
      onFetchTransactionLineItems={onFetchTransactionLineItems}
      lineItems={lineItems}
      fetchLineItemsInProgress={fetchLineItemsInProgress}
      fetchLineItemsError={fetchLineItemsError}
      urls={urls}
      paymentRequired={isPaymentRequired}
      shippingRequired={isShippingRequired}
    />
  ) : (
    loadingOrFailedFetching
  );



  return (
    <Page
      title={intl.formatMessage({ id: 'TransactionPage.title' }, { title: listingTitle })}
      scrollingDisabled={scrollingDisabled}
    >
      {isOwnOrder ? (
        <LayoutSingleColumn>
          <LayoutWrapperTopbar>
            <TopbarContainer />
          </LayoutWrapperTopbar>
          <LayoutWrapperMain className={css.wrapperMain}>
            <div className={css.root}>{panel}</div>
            <ReviewModal
              id="ReviewOrderModal"
              isOpen={state.isReviewModalOpen}
              onCloseModal={() =>
                setState(prevState => ({
                  ...prevState,
                  isReviewModalOpen: false,
                }))
              }
              onManageDisableScrolling={onManageDisableScrolling}
              onSubmitReview={onSubmitReview}
              revieweeName={otherUserDisplayName}
              reviewSent={state.reviewSubmitted}
              sendReviewInProgress={sendReviewInProgress}
              sendReviewError={sendReviewError}
            />
            <DisputeModal
              id="DisputeOrderModal"
              isOpen={state.isDisputeModalOpen}
              onCloseModal={() =>
                setState(prevState => ({
                  ...prevState,
                  isDisputeModalOpen: false,
                }))
              }
              onManageDisableScrolling={onManageDisableScrolling}
              onDisputeOrder={onDisputeOrder}
              disputeSubmitted={state.disputeSubmitted}
              disputeInProgress={disputeInProgress}
              disputeError={disputeError}
            />
          </LayoutWrapperMain>
          <LayoutWrapperFooter className={css.footer}>
            <Footer />
          </LayoutWrapperFooter>
        </LayoutSingleColumn>
      ) : (
        <LayoutSideNavigation>
          <LayoutWrapperTopbar>
            <TopbarContainer />
          </LayoutWrapperTopbar>
          <LayoutWrapperSideNav>
            <ShopNav selectedPageName="SellerDashboardPage" />
          </LayoutWrapperSideNav>
          <LayoutWrapperMain className={css.wrapperMain}>
            <div className={css.root}>{panel}</div>
            <ReviewModal
              id="ReviewOrderModal"
              isOpen={state.isReviewModalOpen}
              onCloseModal={() =>
                setState(prevState => ({
                  ...prevState,
                  isReviewModalOpen: false,
                }))
              }
              onManageDisableScrolling={onManageDisableScrolling}
              onSubmitReview={onSubmitReview}
              revieweeName={otherUserDisplayName}
              reviewSent={state.reviewSubmitted}
              sendReviewInProgress={sendReviewInProgress}
              sendReviewError={sendReviewError}
            />
            <DisputeModal
              id="DisputeOrderModal"
              isOpen={state.isDisputeModalOpen}
              onCloseModal={() =>
                setState(prevState => ({
                  ...prevState,
                  isDisputeModalOpen: false,
                }))
              }
              onManageDisableScrolling={onManageDisableScrolling}
              onDisputeOrder={onDisputeOrder}
              disputeSubmitted={state.disputeSubmitted}
              disputeInProgress={disputeInProgress}
              disputeError={disputeError}
            />
          </LayoutWrapperMain>
          <LayoutWrapperFooter className={css.footer}>
            <Footer />
          </LayoutWrapperFooter>
        </LayoutSideNavigation>
      )}
    </Page>
  );
};

TransactionPageComponent.defaultProps = {
  currentUser: null,
  fetchTransactionError: null,
  disputeError: null,
  markDeliveredError: null,
  markReceivedError: null,
  markReceivedFromPurchasedError: null,
  transaction: null,
  fetchMessagesError: null,
  initialMessageFailedToTransaction: null,
  savePaymentMethodFailed: false,
  sendMessageError: null,
  timeSlots: null,
  fetchTimeSlotsError: null,
  lineItems: null,
  fetchLineItemsError: null,
};

TransactionPageComponent.propTypes = {
  params: shape({ id: string }).isRequired,
  transactionRole: oneOf([PROVIDER, CUSTOMER]).isRequired,
  currentUser: propTypes.currentUser,
  fetchTransactionError: propTypes.error,
  markReceivedInProgress: bool.isRequired,
  markReceivedError: propTypes.error,
  onMarkReceived: func.isRequired,
  markReceivedFromPurchasedInProgress: bool.isRequired,
  markReceivedFromPurchasedError: propTypes.error,
  onMarkReceivedFromPurchased: func.isRequired,
  markDeliveredInProgress: bool.isRequired,
  markDeliveredError: propTypes.error,
  onMarkDelivered: func.isRequired,
  disputeInProgress: bool.isRequired,
  disputeError: propTypes.error,
  onDispute: func.isRequired,
  scrollingDisabled: bool.isRequired,
  transaction: propTypes.transaction,
  fetchMessagesError: propTypes.error,
  totalMessagePages: number.isRequired,
  oldestMessagePageFetched: number.isRequired,
  messages: arrayOf(propTypes.message).isRequired,
  initialMessageFailedToTransaction: propTypes.uuid,
  savePaymentMethodFailed: bool,
  sendMessageInProgress: bool.isRequired,
  sendMessageError: propTypes.error,
  onShowMoreMessages: func.isRequired,
  onSendMessage: func.isRequired,
  timeSlots: arrayOf(propTypes.timeSlot),
  fetchTimeSlotsError: propTypes.error,
  callSetInitialValues: func.isRequired,
  onInitializeCardPaymentData: func.isRequired,
  onFetchTransactionLineItems: func.isRequired,

  // line items
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const {
    fetchTransactionError,
    disputeInProgress,
    disputeError,
    markReceivedInProgress,
    markReceivedError,
    markReceivedFromPurchasedInProgress,
    markReceivedFromPurchasedError,
    markDeliveredInProgress,
    markDeliveredError,
    transactionRef,
    fetchMessagesInProgress,
    fetchMessagesError,
    totalMessagePages,
    oldestMessagePageFetched,
    messages,
    initialMessageFailedToTransaction,
    savePaymentMethodFailed,
    sendMessageInProgress,
    sendMessageError,
    sendReviewInProgress,
    sendReviewError,
    timeSlots,
    fetchTimeSlotsError,
    processTransitions,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    trackingNumber,
    urls,
  } = state.TransactionPage;
  const { currentUser } = state.user;

  const transactions = getMarketplaceEntities(state, transactionRef ? [transactionRef] : []);
  const transaction = transactions.length > 0 ? transactions[0] : null;

  return {
    currentUser,
    fetchTransactionError,
    disputeInProgress,
    disputeError,
    markReceivedInProgress,
    markReceivedError,
    markReceivedFromPurchasedInProgress,
    markReceivedFromPurchasedError,
    markDeliveredInProgress,
    markDeliveredError,
    scrollingDisabled: isScrollingDisabled(state),
    transaction,
    fetchMessagesInProgress,
    fetchMessagesError,
    totalMessagePages,
    oldestMessagePageFetched,
    messages,
    initialMessageFailedToTransaction,
    savePaymentMethodFailed,
    sendMessageInProgress,
    sendMessageError,
    sendReviewInProgress,
    sendReviewError,
    timeSlots,
    fetchTimeSlotsError,
    processTransitions,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    trackingNumber,
    urls,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    onDispute: (transactionId, disputeReason) => dispatch(dispute(transactionId, disputeReason)),
    onMarkReceived: transactionId => dispatch(markReceived(transactionId)),
    onMarkReceivedFromPurchased: transactionId =>
      dispatch(markReceivedFromPurchased(transactionId)),
    onMarkDelivered: transactionId => dispatch(markDelivered(transactionId, trackingNumber)),
    onShowMoreMessages: txId => dispatch(fetchMoreMessages(txId)),
    onSendMessage: (txId, message) => dispatch(sendMessage(txId, message)),
    onManageDisableScrolling: (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    onSendReview: (role, tx, reviewRating, reviewContent) =>
      dispatch(sendReview(role, tx, reviewRating, reviewContent)),
    callSetInitialValues: (setInitialValues, values) => dispatch(setInitialValues(values)),
    onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
    onFetchTransactionLineItems: (orderData, isOwnListing) =>
      dispatch(fetchTransactionLineItems(orderData, isOwnListing)),
    getUrls: (listingsIds, transactionId, userId) =>
      dispatch(getUrls(listingsIds, transactionId, userId)),
  };
};

const TransactionPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(TransactionPageComponent);

export default TransactionPage;
